import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Zap, Star, Crown, RefreshCw, Sparkles, ArrowUpRight, ArrowDownRight, Gift } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Settings → Coach Credits section.
 *
 * Read-only transparency view for the Coach AI credits system. Reads ONLY
 * from `GET /api/credits/me` — same endpoint the in-page badge uses — so
 * there's a single source of truth and no risk of drift between the two
 * surfaces.
 *
 * No raw message text is shown (the backend ledger deliberately doesn't
 * store it). Each ledger row surfaces only: kind, tier, delta, ts.
 */
export default function SettingsCreditsSection() {
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/credits/me");
      setSnap(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Failed to load credits");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <section className="brutal-card bg-white p-5 mb-5" data-testid="settings-credits-section">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="display text-xl font-extrabold flex items-center gap-2">
            <Sparkles size={20} className="text-[color:var(--brand-blue,#2563EB)]" />
            Coach AI credits
          </h2>
          <p className="text-xs text-[color:var(--ink-soft)] mt-1 max-w-xl leading-snug">
            Credits power the AI Coach only. Your board, voice, photos, and
            offline mode always work regardless of credit balance.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="pill-btn"
          disabled={loading}
          aria-label="Reload credits"
          data-testid="settings-credits-reload"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {error && (
        <div className="brutal-card bg-[#FFE3E0] text-[color:var(--coral-deep)] p-3 mb-3 text-sm" data-testid="settings-credits-error">
          {error}
        </div>
      )}

      {snap && (
        <>
          <BalanceRow snap={snap} />
          <TierCosts costs={snap.tier_costs} />
          <LedgerList rows={(snap.ledger || []).slice(0, 10)} />
        </>
      )}
      {!snap && !loading && !error && (
        <p className="text-sm text-[color:var(--ink-soft)]">No credit data yet.</p>
      )}
    </section>
  );
}

function BalanceRow({ snap }) {
  const balance = snap.balance || 0;
  const trial = snap.free_trial_remaining || 0;
  const zero = balance + trial === 0;
  return (
    <div className="grid grid-cols-2 gap-3 mb-4">
      <div
        className={`brutal-card p-3 ${zero ? "bg-[#FFE3E0]" : "bg-[color:var(--paper-muted,#F2EFE7)]"}`}
        data-testid="settings-credits-balance"
      >
        <div className="text-[10px] uppercase tracking-wider font-bold text-[color:var(--ink-soft)]">Paid balance</div>
        <div className="display text-3xl font-extrabold tabular-nums" data-testid="settings-credits-balance-value">{balance}</div>
        <div className="text-xs text-[color:var(--ink-soft)]">credits</div>
      </div>
      <div className="brutal-card bg-[color:var(--paper-muted,#F2EFE7)] p-3" data-testid="settings-credits-trial">
        <div className="text-[10px] uppercase tracking-wider font-bold text-[color:var(--ink-soft)]">Granted credits</div>
        <div className="display text-3xl font-extrabold tabular-nums text-[color:var(--brand-blue,#2563EB)]" data-testid="settings-credits-trial-value">{trial}</div>
        <div className="text-xs text-[color:var(--ink-soft)]">
          {trial > 0 ? "Fast tier only" : "Used"}
        </div>
      </div>
    </div>
  );
}

const TIER_ICON = { cheap: Zap, standard: Star, premium: Crown };
const TIER_LABEL = { cheap: "Fast (gpt-5.2)", standard: "Standard (Claude Sonnet)", premium: "Premium (Claude Opus)" };

function TierCosts({ costs }) {
  if (!costs) return null;
  return (
    <div className="mb-4 text-xs">
      <div className="text-[10px] uppercase tracking-wider font-bold text-[color:var(--ink-soft)] mb-1">Per-message cost</div>
      <div className="flex flex-wrap gap-1.5" data-testid="settings-credits-tier-costs">
        {["cheap", "standard", "premium"].map((t) => {
          const Icon = TIER_ICON[t];
          return (
            <span
              key={t}
              className="inline-flex items-center gap-1.5 brutal-card bg-white px-2 py-1 text-xs"
              data-testid={`settings-credits-tier-cost-${t}`}
            >
              <Icon size={12} /> {TIER_LABEL[t]} · {costs[t]}cr
            </span>
          );
        })}
      </div>
    </div>
  );
}

const KIND_META = {
  free_trial_grant: { label: "Credits granted", icon: Gift,            tone: "text-[color:var(--brand-blue,#2563EB)]" },
  topup:            { label: "Top-up",             icon: ArrowUpRight,    tone: "text-[color:var(--brand-green,#3FA34D)]" },
  coach_burn:       { label: "Coach message",      icon: ArrowDownRight,  tone: "text-[color:var(--ink-soft)]" },
};

function LedgerList({ rows }) {
  if (!rows || rows.length === 0) {
    return (
      <div>
        <div className="text-[10px] uppercase tracking-wider font-bold text-[color:var(--ink-soft)] mb-1">Recent activity</div>
        <p className="text-sm text-[color:var(--ink-soft)]" data-testid="settings-credits-ledger-empty">
          No activity yet. Try the Coach to use your granted credits.
        </p>
      </div>
    );
  }
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-[color:var(--ink-soft)] mb-1">Recent activity</div>
      <ul className="divide-y divide-[color:var(--paper-muted,#E5E1D8)]" data-testid="settings-credits-ledger">
        {rows.map((r) => {
          const meta = KIND_META[r.kind] || { label: r.kind, icon: ArrowDownRight, tone: "text-[color:var(--ink-soft)]" };
          const Icon = meta.icon;
          const delta = (r.delta_balance || 0) + (r.delta_free_trial || 0);
          const sign = delta > 0 ? "+" : "";
          const tierTag = r.tier ? ` · ${r.tier}` : "";
          const ts = r.ts ? new Date(r.ts).toLocaleString() : "";
          return (
            <li key={r.id} className="py-2 flex items-center gap-3" data-testid={`settings-credits-ledger-row-${r.id}`}>
              <Icon size={14} className={meta.tone} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{meta.label}{tierTag}</div>
                <div className="text-[11px] text-[color:var(--ink-soft)]">{ts}</div>
              </div>
              <div className={`text-sm font-extrabold tabular-nums ${delta >= 0 ? "text-[color:var(--brand-green,#3FA34D)]" : "text-[color:var(--ink)]"}`}>
                {sign}{delta}
              </div>
            </li>
          );
        })}
      </ul>
      <div className="mt-3 text-[11px] text-[color:var(--ink-soft)] leading-snug">
        We never store the text of your Coach messages in this log — only the
        tier, model, and credit cost. <Link to="/privacy" className="underline">Privacy policy</Link>.
      </div>
    </div>
  );
}
